import { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios';
import { Graphback } from '../graphback';

/**
 * A GraphQL failure carrying the server's `extensions.code`. A plain `Error`
 * with one added property rather than a subclass, so every `instanceof Error`
 * and `message` caller is untouched.
 */
export type GraphQLRequestError = Error & { code?: string };

/** The server's `extensions.code`, when the throw came from one. */
export const graphQLErrorCode = (error: unknown): string | undefined =>
  error instanceof Error ? (error as GraphQLRequestError).code : undefined;

/**
 * Sees every request `requestGraphQL` makes and decides its answer: call
 * `next` to send it, or answer without the server. The app's offline fetcher
 * is the one caller.
 */
export type GraphQLRequestInterceptor = (
  query: string,
  variables: unknown,
  next: () => Promise<unknown>
) => Promise<unknown>;

let interceptor: GraphQLRequestInterceptor | null = null;

/** Installs (or, with null, removes) the one interceptor every hook's request goes through. */
export const setGraphQLRequestInterceptor = (
  fn: GraphQLRequestInterceptor | null
) => {
  interceptor = fn;
};

/**
 * One GraphQL request straight to the server, never through the interceptor:
 * for senders whose answer must come from the server or not at all (the
 * app's outbox replay and its offline-copy downloads). Pass `config.signal` to
 * bound it.
 */
export const requestGraphQLDirect = async <TData, TVariables = unknown>(
  query: string,
  variables?: TVariables,
  config?: Partial<AxiosRequestConfig>
): Promise<TData> => {
  const res = await Graphback.getInstance().post(
    '',
    { query, variables },
    config
  );

  const json = res.data;

  if (json.errors) {
    const { message, extensions } = (json.errors[0] || {}) as {
      message?: string;
      extensions?: { code?: string };
    };
    const error: GraphQLRequestError = new Error(message || 'Error…');
    if (extensions?.code) error.code = extensions.code;
    throw error;
  }

  return json.data;
};

/**
 * One GraphQL request, through the interceptor when one is installed; every
 * hook sends this way. Pass `config.signal` to bound it.
 */
export const requestGraphQL = async <TData, TVariables = unknown>(
  query: string,
  variables?: TVariables,
  config?: Partial<AxiosRequestConfig>
): Promise<TData> => {
  const next = () =>
    requestGraphQLDirect<TData, TVariables>(query, variables, config);
  if (!interceptor) return next();
  return (await interceptor(query, variables, next)) as TData;
};

export const useFetcher = <TData, TVariables>(
  query: string,
  options?: RequestInit['headers']
): ((variables?: TVariables) => Promise<TData>) => {
  let config: Partial<AxiosRequestConfig>;

  if (options) {
    const headers = { ...options } as RawAxiosRequestHeaders;
    config = {
      headers,
    };
  }

  return (variables?: TVariables) =>
    requestGraphQL<TData, TVariables>(query, variables, config);
};
